/**
 * CALL API — Wrapper REST allineato a CallsResourceImpl.java (commit `090a0e38`).
 * I metodi `getById`/`create`/`update`/`remove` vengono da `createCrudApi`; solo
 * `getAll` è ridefinito perché la list ha `direction` nel PATH (non standard).
 *
 * Contratto backend (rest-openapi.yaml):
 *   - GET    /calls/{groupId}/{direction}?status=&search=&page=&pageSize=  -> Page<Call>
 *   - POST   /calls/{groupId}  CallInput                                    -> Call
 *   - GET    /call/{callId}                                                  -> Call
 *   - PATCH  /call/{callId}    CallInput (parziale)                         -> Call
 *   - DELETE /call/{callId}                                                  -> 200
 *
 * Differenza rispetto a Note/Task: la list ha `direction` nel PATH (non
 * come query), allineato al pattern Messages. Senza direction la rotta
 * non matcha — `getAll` la richiede. `callUpdateToInput` ignora `callId` (whitelist).
 */

import type { Page, ServerListParams } from '@crm/shared';
import { httpClient } from '../../api/httpClient';
import { createCrudApi } from '../../store/createCrudApi';
import {
  callFromDto,
  callCreateToInput,
  callUpdateToInput,
  type CallDto,
} from './callMappers';
import type { Call, CallCreatePayload, CallDirection, CallStatus, CallUpdatePayload } from './callTypes';

const COLLECTION = '/calls';
const SINGLE = '/call';

/**
 * Parametri di list. `direction` formalmente opzionale per essere
 * compatibile con la firma `CrudApi.getAll(groupId, params?)` usata da
 * `createCrudThunks` / `registerEntityType`. Quando assente, il client
 * applica default 'OUT' (caso piu' frequente nel CRM). I consumer che
 * vogliono IN passano `direction: 'IN'` esplicitamente.
 */
export interface CallListParams extends ServerListParams {
  direction?: CallDirection;
  status?: CallStatus;
}

const base = createCrudApi<Call, CallDto, CallCreatePayload, CallUpdatePayload, CallListParams>({
  collection: COLLECTION,
  single: SINGLE,
  fromDto: callFromDto,
  createToInput: callCreateToInput,
  updateId: (p) => p.callId,
  updateToInput: (p) => callUpdateToInput(p),
});

export const callApi = {
  ...base,

  /**
   * Override: la list ha `direction` come segmento di PATH
   * (`/calls/{groupId}/{direction}`), forma che la factory non copre. Default
   * 'OUT' quando omessa; gli altri filtri viaggiano come query.
   */
  getAll: (groupId: number, params?: CallListParams): Promise<Page<Call>> => {
    const { direction = 'OUT', ...rest } = params ?? {};
    return httpClient
      .get<Page<CallDto>>(`${COLLECTION}/${groupId}/${direction}`, {
        params: rest as Record<string, unknown>,
      })
      .then((r) => ({
        ...r.data,
        items: r.data.items.map(callFromDto),
      }));
  },
};
